import {
  getBudgetCurrentStatus,
  type StatusBudget,
  type versionInfo,
} from "../../../types/budget/budget";
import type { BudgetListItem } from "../../../utils/budget-adapters";

/**
 * Fields comparison modals read from `budgetList` (FE-P1-09).
 * Selection / tabs / labels only — comparison payloads come from export APIs by id.
 * Intentionally excludes `budgetServices`. Compatible with v1 `Budget` and v2 `BudgetSummary`.
 */
export type ComparisonModalBudgetRow = {
  _id?: string;
  name?: string;
  budgetIndex?: number;
  versionHistory?: versionInfo[];
  currentVersionStatus?: StatusBudget;
};

export type ComparisonModalBudgetRowWithId = ComparisonModalBudgetRow & {
  _id: string;
};

/** Narrow list prop shared by Budget / Negotiation / Closing comparison modals. */
export type ComparisonModalBudgetList = ReadonlyArray<
  ComparisonModalBudgetRow | BudgetListItem
>;

/**
 * Effective status for list filtering — prefers `currentVersionStatus`, else
 * latest `versionHistory` entry (never `versionHistory[0]`, which stays OPEN).
 */
export function getComparisonListStatus(
  row: ComparisonModalBudgetRow,
): StatusBudget {
  if (row.currentVersionStatus) return row.currentVersionStatus;
  return getBudgetCurrentStatus(row.versionHistory);
}

/**
 * Budgets eligible for the Orçamento (budget) comparison modal selection step.
 * Matches prior intent (exclude DELETED / APPROVED) using current status, not `[0]`.
 */
export function filterBudgetsForBudgetComparison(
  budgetList: ComparisonModalBudgetList | undefined | null,
): ComparisonModalBudgetRowWithId[] {
  return (budgetList || []).filter((b): b is ComparisonModalBudgetRowWithId => {
    if (!b._id) return false;
    const st = getComparisonListStatus(b);
    return !["DELETED", "APPROVED"].includes(st);
  });
}

/** Display name for a list row (summary-safe). */
export function comparisonBudgetDisplayName(
  row: ComparisonModalBudgetRow | undefined | null,
  fallbackIndex = 0,
): string {
  if (!row) return `Orçamento ${fallbackIndex + 1}`;
  return row.name || `Orçamento ${(row.budgetIndex ?? fallbackIndex) + 1}`;
}

export function findComparisonBudget(
  budgetList: ComparisonModalBudgetList | undefined | null,
  budgetId: string,
): ComparisonModalBudgetRow | undefined {
  return (budgetList || []).find((b) => b._id === budgetId);
}

export function comparisonBudgetVersionHistory(
  row: ComparisonModalBudgetRow | undefined | null,
): versionInfo[] {
  return row?.versionHistory || [];
}
